import React from 'react';
import { Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { IconCreditCard, IconShieldLock, IconSend, IconBuildingBank, IconAlertTriangle } from '@tabler/icons-react';
import { IHostEarningsWhere } from '../interfaces/heavyDutyMachinery/IHostEarnings';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { formatCurrency } from '../helpers/currencyHelper';
import { SURFACE_1_SOFT, TEAL, TEAL_SOFT_BG } from '../theme/creamyTokens';

const { Title, Text } = Typography;

interface IBucket {
  key: keyof IHostEarningsWhere & string;
  amount: number;
  count: number;
  Icon: React.ElementType;
  labelKey: string;
  detailKey: string;
  /** Arrived: the money is where it was going. */
  settled?: boolean;
  /** Somebody has to act on this one. */
  alarming?: boolean;
}

interface IHostEarningsWhereCardProps {
  where: IHostEarningsWhere;
  currencyCode: string;
}

/**
 * "¿Dónde está mi dinero?" for a whole host, in the same five-stop vocabulary
 * BookingMoneyCard already uses for a single rental -- deliberately the same
 * icons and the same settled/in-flight colour rule, so a host who has read
 * one rental's card can read this one without learning anything new.
 *
 * The buckets are disjoint and they add up (see the backend's
 * HostEarningsCalculator): every euro this host is owed is in exactly one of
 * them. A bucket at zero is dropped rather than shown as a row of zeros --
 * except "stuck", which is shown whenever it is non-zero and never softened,
 * because it is the only one that needs somebody to do something.
 */
const HostEarningsWhereCard: React.FC<IHostEarningsWhereCardProps> = ({ where, currencyCode }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();

  const buckets: IBucket[] = [
    {
      key: 'upcoming',
      amount: where.upcoming,
      count: where.upcomingCount,
      Icon: IconCreditCard,
      labelKey: TRANSLATIONS.HOST_EARNINGS_WHERE_UPCOMING,
      detailKey: TRANSLATIONS.HOST_EARNINGS_WHERE_UPCOMING_DETAIL,
    },
    {
      key: 'heldByPlatform',
      amount: where.heldByPlatform,
      count: where.heldByPlatformCount,
      Icon: IconShieldLock,
      labelKey: TRANSLATIONS.HOST_EARNINGS_WHERE_HELD,
      detailKey: TRANSLATIONS.HOST_EARNINGS_WHERE_HELD_DETAIL,
    },
    {
      key: 'onTheWay',
      amount: where.onTheWay,
      count: where.onTheWayCount,
      Icon: IconSend,
      labelKey: TRANSLATIONS.HOST_EARNINGS_WHERE_ON_THE_WAY,
      detailKey: TRANSLATIONS.HOST_EARNINGS_WHERE_ON_THE_WAY_DETAIL,
    },
    {
      key: 'paidOut',
      amount: where.paidOut,
      count: where.paidOutCount,
      Icon: IconBuildingBank,
      labelKey: TRANSLATIONS.HOST_EARNINGS_WHERE_PAID_OUT,
      detailKey: TRANSLATIONS.HOST_EARNINGS_WHERE_PAID_OUT_DETAIL,
      settled: true,
    },
    {
      key: 'stuck',
      amount: where.stuck,
      count: where.stuckCount,
      Icon: IconAlertTriangle,
      labelKey: TRANSLATIONS.HOST_EARNINGS_WHERE_STUCK,
      detailKey: TRANSLATIONS.HOST_EARNINGS_WHERE_STUCK_DETAIL,
      alarming: true,
    },
  ];

  const visible = buckets.filter((bucket) => bucket.amount !== 0 || bucket.count > 0);

  return (
    <div
      style={{
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 16,
        padding: 16,
      }}
    >
      <Title level={5} style={{ margin: '0 0 4px' }}>
        {translate(t, TRANSLATIONS.HOST_EARNINGS_WHERE_TITLE)}
      </Title>
      <Text type="secondary" style={{ fontSize: 13 }}>
        {translate(t, TRANSLATIONS.HOST_EARNINGS_WHERE_SUBTITLE)}
      </Text>

      {visible.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 14 }}>
          {translate(t, TRANSLATIONS.HOST_EARNINGS_WHERE_EMPTY)}
        </Text>
      ) : (
        visible.map((bucket, index) => (
          <div
            key={bucket.key}
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'flex-start',
              marginTop: 14,
              paddingTop: index === 0 ? 0 : 14,
              borderTop: index === 0 ? undefined : `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            <span
              style={{
                display: 'flex',
                flexShrink: 0,
                width: 40,
                height: 40,
                borderRadius: '50%',
                alignItems: 'center',
                justifyContent: 'center',
                background: bucket.alarming ? token.colorErrorBg : bucket.settled ? TEAL_SOFT_BG : SURFACE_1_SOFT,
                color: bucket.alarming ? token.colorError : bucket.settled ? TEAL : token.colorTextSecondary,
              }}
            >
              <bucket.Icon size={20} />
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                <Text strong style={{ fontSize: 15 }}>
                  {translate(t, bucket.labelKey)}
                </Text>
                <Text
                  strong
                  style={{
                    fontSize: 15,
                    whiteSpace: 'nowrap',
                    fontVariantNumeric: 'tabular-nums',
                    color: bucket.alarming ? token.colorError : undefined,
                  }}
                >
                  {formatCurrency(bucket.amount, currencyCode, i18n.language)}
                </Text>
              </div>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {t(bucket.detailKey, { count: bucket.count })}
              </Text>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default HostEarningsWhereCard;
